import {
  type ComponentType,
  createContext,
  type ReactNode,
  type Ref,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Chip, ChipInput, cn, Search, Tooltip } from '@ondit/ui'
import { HEADER_ACTION_CLUSTER } from '@/components/page-header-bar'
import { useT } from '~/core/i18n'

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export interface SettingsAction {
  /** Stable render identity. Falls back to `text`, which remounts the chip whenever the label flips (Save → Saving...). */
  id?: string
  text: string
  textTone?: 'error'
  icon?: ComponentType<{ className?: string }>
  variant?: 'primary' | 'destructive'
  active?: boolean
  onSelect: () => void
  disabled?: boolean
  tooltip?: string
  onPrefetch?: () => void
}

export interface SettingsHeaderSearch {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
}

export interface SettingsBackAction {
  text: string
  icon?: ComponentType<{ className?: string }>
  onSelect: () => void
}

export interface SettingsHeaderConfig {
  title?: string
  description?: string
  docsLink?: string
  back?: SettingsBackAction
  actions?: SettingsAction[]
  search?: SettingsHeaderSearch
  scrollContainerRef?: Ref<HTMLDivElement>
}

const EMPTY_CONFIG: SettingsHeaderConfig = {}
const RegisterContext = createContext<((config: SettingsHeaderConfig | null) => void) | null>(null)

interface ReadContextValue {
  /** `null` means no body currently owns the header. */
  configRef: { current: SettingsHeaderConfig | null }
  signature: string
}

const ReadContext = createContext<ReadContextValue | null>(null)

function computeSignature(config: SettingsHeaderConfig | null): string {
  if (config === null) return 'released'
  return JSON.stringify({
    title: config.title ?? '',
    description: config.description ?? '',
    docsLink: config.docsLink ?? '',
    back: config.back ? [config.back.text, config.back.icon ? 1 : 0] : null,
    actions: config.actions?.map((action) => [
      action.text,
      // `id` participates in ordering, so a config that changes only the id
      // must still re-render — the sort key cannot be wider than the signature.
      action.id ?? '',
      action.textTone ?? '',
      action.variant ?? '',
      action.active ?? false,
      action.disabled ?? false,
      action.icon ? 1 : 0,
      action.tooltip ?? '',
      action.onPrefetch ? 1 : 0,
    ]),
    search: config.search
      ? [config.search.value, config.search.placeholder ?? '', config.search.disabled ?? false]
      : null,
  })
}

export function SettingsHeaderProvider({ children }: { children: ReactNode }) {
  const configRef = useRef<SettingsHeaderConfig | null>(null)
  const [signature, setSignature] = useState('')

  const register = useCallback((config: SettingsHeaderConfig | null) => {
    configRef.current = config
    const next = computeSignature(config)
    setSignature((previous) => (previous === next ? previous : next))
  }, [])

  const readValue = useMemo<ReadContextValue>(() => ({ configRef, signature }), [signature])

  return (
    <RegisterContext.Provider value={register}>
      <ReadContext.Provider value={readValue}>{children}</ReadContext.Provider>
    </RegisterContext.Provider>
  )
}

export function useSettingsHeader(config: SettingsHeaderConfig) {
  const register = useContext(RegisterContext)

  /**
   * A layout effect, not a passive one, and load-bearing for that reason: on a section swap
   * the shell renders before the outgoing body's cleanup runs, so `configRef` still holds the
   * previous section's config during that render. Flushing the release synchronously before
   * paint is what stops the previous section's title showing for a frame. Downgrading this to
   * `useEffect` reintroduces that flicker.
   */
  useIsomorphicLayoutEffect(() => {
    register?.(config)
  })

  useIsomorphicLayoutEffect(() => {
    return () => register?.(null)
  }, [register])
}

interface SettingsActionChipProps {
  /** Presentation fields plus the default `onSelect` / `onPrefetch` handlers. */
  action: SettingsAction
  /** Overrides `action.onSelect` — the header shell passes a ref-reading indirection to avoid stale closures. */
  onSelect?: () => void
  /** Overrides `action.onPrefetch`, same reason. */
  onPrefetch?: () => void
}

/**
 * The one chip rendering of a {@link SettingsAction}. Both header stacks render
 * through this, so an action's chrome — tone, icon, variant, disabled, tooltip —
 * is identical wherever it is mounted. Container spacing still belongs to the
 * enclosing shell.
 */
export function SettingsActionChip({
  action,
  onSelect = action.onSelect,
  onPrefetch = action.onPrefetch,
}: SettingsActionChipProps) {
  // The labels are the Agent's English and are keyed that way in AGENT_COPY;
  // translating here covers every header's chips at once.
  const t = useT()
  const chip = (
    <Chip
      variant={action.variant}
      active={action.active}
      leftIcon={action.icon}
      onClick={onSelect}
      onMouseEnter={onPrefetch}
      onFocus={onPrefetch}
      disabled={action.disabled}
      // A disabled <button> is not a hit-test target, so the tooltip's wrapping
      // span would never see pointerenter and the explanation would never show.
      className={cn(action.tooltip && action.disabled && 'pointer-events-none')}
    >
      {action.textTone === 'error' ? (
        <span className='text-[var(--text-error)]'>{t(action.text)}</span>
      ) : (
        t(action.text)
      )}
    </Chip>
  )
  if (!action.tooltip) return chip
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <span className='inline-flex'>{chip}</span>
      </Tooltip.Trigger>
      <Tooltip.Content>{action.tooltip}</Tooltip.Content>
    </Tooltip.Root>
  )
}

/**
 * Renders a {@link SettingsAction} list as chips using each action's own
 * handlers. This is the data path for headers that take a `ReactNode`
 * (`CredentialDetailLayout`) — reach for it instead of hand-rolling `Chip`s, so
 * those surfaces keep the same chrome as the settings shell. The shell itself
 * maps through {@link SettingsActionChip} directly, since it must route every
 * handler through a ref to avoid stale closures.
 */
export function SettingsActionChips({ actions }: { actions: SettingsAction[] }) {
  return (
    <>
      {orderHeaderActions(actions).map(({ action }) => (
        <SettingsActionChip key={action.id ?? action.text} action={action} />
      ))}
    </>
  )
}

/**
 * Every header reads left→right as
 * `[secondary actions] → [Delete] → [Discard] → [Save]`.
 *
 * Delete is placed by its `id`, not by where the caller happened to put it, so a
 * page with no primary action still can't leave a destructive chip in the slot a
 * primary would occupy.
 *
 * The shell enforces it rather than trusting callsites, because the natural way
 * to write the array — spreading {@link saveDiscardActions} first, then adding a
 * Delete — produces the opposite order and puts a destructive chip to the right
 * of the primary one. Ranking is stable, so an action's position within its own
 * band is still the caller's to choose.
 *
 * Pairs each action with its ORIGINAL index: the shell dereferences
 * `actions[index]` on a live ref to dodge stale closures, so a reordered render
 * must not renumber them.
 */
export function orderHeaderActions(
  actions: SettingsAction[] | undefined
): { action: SettingsAction; index: number }[] {
  const rank = (action: SettingsAction) => {
    if (action.variant === 'primary') return 3
    if (action.id === 'discard') return 2
    if (action.id === 'delete') return 1
    return 0
  }
  return (actions ?? [])
    .map((action, index) => ({ action, index }))
    .sort((a, b) => rank(a.action) - rank(b.action))
}

/**
 * The registered search and actions of a section drawn inside a page that owns
 * its heading — the application's settings page, which titles every section
 * from its catalogue. Search on the left and the actions on the right, as on
 * every console toolbar; a registered title is not drawn, because it would be
 * the page's heading a second time.
 *
 * Handlers are read through the live ref: a registration re-renders this only
 * when its signature changes, so a handler captured at render would be stale.
 */
export function SettingsHeaderToolbar({ children }: { children: ReactNode }) {
  const t = useT()
  const configRef = useContext(ReadContext)?.configRef
  const { actions, search } = configRef?.current ?? EMPTY_CONFIG
  const ordered = orderHeaderActions(actions)

  return (
    <div className='flex flex-col gap-4'>
      {(search || ordered.length > 0) && (
        <div className='flex items-center gap-3'>
          <div className='min-w-0 flex-1'>
            {search && (
              <ChipInput
                icon={Search}
                placeholder={t(search.placeholder ?? 'Search...')}
                value={search.value}
                onChange={(event) => configRef?.current?.search?.onChange(event.target.value)}
                disabled={search.disabled}
                autoComplete='off'
                className='w-full'
              />
            )}
          </div>
          <div className={HEADER_ACTION_CLUSTER}>
            {ordered.map(({ action, index }) => (
              <SettingsActionChip
                key={action.id ?? action.text}
                action={action}
                onSelect={() => configRef?.current?.actions?.[index]?.onSelect()}
                onPrefetch={
                  action.onPrefetch
                    ? () => configRef?.current?.actions?.[index]?.onPrefetch?.()
                    : undefined
                }
              />
            ))}
          </div>
        </div>
      )}
      {children}
    </div>
  )
}
